import React, {useEffect, useState, useCallback} from "react";
import {API} from "aws-amplify";
import config from "../../../config";
import MaterialTable, {MTableToolbar} from "material-table";
import CustomizedSnackbars from "../../CommonUtilities/CustomizedSnackbars";
import {REMOVE_DROP_SHADOW} from "../../Material-TableCustomization/CSSChanges/RemoveDropShadow";
import {ICONS_OVERRIDE_SECONDARY_COLOR} from "../../Material-TableCustomization/IconsOverride/IconsOverrideSecondaryColor";
import {LOCALIZATION} from "../../Material-TableCustomization/Localization/LocalizationES";
import {STUDENT_PAYMENTS_COLUMN_DEFINITION_PROPS} from "../../Material-TableCustomization/ColumnsDefinition/StudentsPaymentsColumnDefinition";
import {API_BASE_PATH} from "../../CommonUtilities/UtilityFunctions";
import ModalNewPayment from "../../Modals/ModalNewPayment";
import ModalMultiplePayments from "../../Modals/ModalMultiplePayments";
import ModalConfirmDeletePayment from "../../Modals/ModalConfirmDeletePayment";

export default function StudentPaymentsTable(props) {
    const [studentPayments, setStudentPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [alertStatus, setAlertStatus] = useState({open: false, message: "", severity: ""});
    const [deleteConfirmation, setDeleteConfirmation] = useState({open: false, paymentData: null});

    const loadStudentPaymentsApi = useCallback(() => {
        return API.get(API_BASE_PATH, `/tesoreria/get-student-payments/${props.studentUuid}`);
    }, [props.studentUuid]);

    const deletePaymentApi = (paymentInfo, justification) => {
        return API.del(API_BASE_PATH, "/tesoreria/payments", {
            body: {...paymentInfo, studentUuid: props.studentUuid, justification}
        });
    };

    const updatePaymentApi = (paymentInfo) => {
        return API.put(API_BASE_PATH, "/tesoreria/payments", {
            body: {...paymentInfo, studentUuid: props.studentUuid}
        });
    };

    const loadStudentPayments = useCallback(async () => {
        setIsLoading(true);
        try {
            const studentPayments = await loadStudentPaymentsApi();
            setStudentPayments(studentPayments);
        } catch (e) {
            alert("No se pudo actualizar la lista de estudiantes. Por favor intente de nuevo.");
        } finally {
            setIsLoading(false);
        }
    }, [loadStudentPaymentsApi]);

    const handleChangeOnAlert = shouldPresentAlert => {
        setAlertStatus(shouldPresentAlert);
    }

    useEffect(() => {
        loadStudentPayments();
    }, [loadStudentPayments]);

    async function handleOnRowUpdate(newData, oldData, resolve, reject) {
        try {
            await updatePaymentApi(newData);
            const index = studentPayments.findIndex(o => o.createdAt === oldData.createdAt);
            setStudentPayments([
                ...studentPayments.slice(0, index),
                newData,
                ...studentPayments.slice(index + 1)
            ]);
            resolve();
        } catch (e) {
            console.log(e);
            alert("No se pudo actualizar el pago, por favor intente de nuevo");
            reject();
        } finally {
            resolve();
        }
    }

    async function onPaymentAdded(newPayment, isMultiple = false) {
        if (isMultiple) {
            setStudentPayments([
                ...newPayment.reverse(), ...studentPayments
            ]);
        } else {
            setStudentPayments([
                newPayment, ...studentPayments
            ]);
        }

    }

    async function handleOnRowDelete(oldData, resolve, reject) {
        // Immediately resolve to bypass MaterialTable's confirmation
        resolve();
        // Then open our custom modal
        setDeleteConfirmation({ open: true, paymentData: oldData });
    }

    async function confirmDeletePayment(justification) {
        const { paymentData } = deleteConfirmation;
        setIsLoading(true);
        try {
            await deletePaymentApi(paymentData, justification);
            const index = studentPayments.findIndex(o => o.createdAt === paymentData.createdAt);
            setStudentPayments([
                ...studentPayments.slice(0, index),
                ...studentPayments.slice(index + 1)
            ]);
            setDeleteConfirmation({ open: false, paymentData: null });
        } catch (e) {
            alert("No se pudo borrar el pago. Por favor intente de nuevo");
        } finally {
            setIsLoading(false);
        }
    }

    const handleOpenPaymentPdf = async (event, rowData, version = null) => {
        setIsLoading(true);
        try {
            // Build the API path and query parameters
            let apiPath = `/tesoreria/pdf/payment/${rowData.studentUuid}?createdAt=${rowData.createdAt}`;
            if (version !== null) {
                apiPath += `&version=${version}`;
            }

            // Call the API endpoint (returns JSON with S3 URL)
            const response = await fetch(`${config.apiGateway.URL}${apiPath}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${errorText}`);
            }

            // Parse the JSON response
            const jsonResponse = await response.json();

            // Extract the S3 URL from the response
            const pdfUrl = jsonResponse.data;

            if (!pdfUrl) {
                throw new Error('No se encontró la URL del PDF en la respuesta');
            }

            // Open the S3 URL in a new tab
            const newWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
            if (newWindow) newWindow.opener = null;
        } catch (error) {
            console.error("Error opening payment PDF:", error);
            setAlertStatus({
                open: true,
                message: `Error al abrir el PDF: ${error.message}`,
                severity: "error"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGenerateInvoice = async (rowData) => {
        setIsLoading(true);
        const { studentUuid, createdAt } = rowData;
        // Use the correct API endpoint that returns the PDF directly
        const apiUrl = `${config.receiptApiGateway.URL}/students/${studentUuid}/payments/${createdAt}/receipt`;

        try {
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'application/pdf',
                    // Add any other necessary headers like Authorization if needed
                },
            });

            if (!response.ok) {
                // Attempt to read error details if possible, otherwise use status text
                let errorBody = response.statusText;
                try {
                    // If the error response is JSON, parse it
                    const errorJson = await response.json();
                    errorBody = errorJson.message || JSON.stringify(errorJson);
                } catch (e) {
                    // If not JSON, try to read as text
                    try {
                        errorBody = await response.text();
                    } catch (e2) {
                        // Fallback if reading body fails
                        console.error("Could not read error response body:", e2);
                    }
                }
                throw new Error(`API call failed with status ${response.status}: ${errorBody}`);
            }

            // Check if the response is actually a PDF
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/pdf")) {
                // Handle cases where the response isn't a PDF as expected
                const responseText = await response.text(); // Read unexpected response
                console.error("Expected PDF, but received:", contentType, responseText);
                throw new Error(`Expected a PDF response, but received ${contentType}.`);
            }

            // Get the response body as a Blob
            const pdfBlob = await response.blob();

            // Create a temporary URL for the Blob
            const pdfUrl = URL.createObjectURL(pdfBlob);

            // Open the PDF in a new tab
            const newWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
            
            // Clean up the object URL after a delay to ensure the new tab has loaded
            if (newWindow) {
                newWindow.opener = null;
                // Wait a bit before revoking to ensure the PDF loads in the new tab
                setTimeout(() => {
                    URL.revokeObjectURL(pdfUrl);
                }, 1000);
            } else {
                // If popup was blocked, fall back to download
                const a = document.createElement('a');
                a.href = pdfUrl;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                
                // Extract filename from Content-Disposition header or use a default
                const contentDisposition = response.headers.get('content-disposition');
                let filename = `recibo-${studentUuid}-${createdAt}.pdf`; // Default filename
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1];
                    }
                }
                a.download = filename;
                
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(pdfUrl);
            }

        } catch (error) {
            console.error("Error generating or displaying invoice:", error);
            setAlertStatus({ open: true, message: `Error al procesar la factura: ${error.message}`, severity: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    function renderStudentPayments() {
        return (
            <div>
                <MaterialTable
                    columns={STUDENT_PAYMENTS_COLUMN_DEFINITION_PROPS(props)}
                    data={studentPayments}
                    title="Pagos"
                    isLoading={isLoading}
                    options={{
                        pageSize: 10,
                        addRowPosition: 'first',
                    }}
                    components={{
                        ...REMOVE_DROP_SHADOW,
                        Toolbar: props => (
                            <div>
                                <MTableToolbar {...props} />
                                <div style={{padding: '0px 10px'}}>
                                    <ModalMultiplePayments
                                        setIsLoading={setIsLoading}
                                        student={props.student}
                                        onPaymentAdded={onPaymentAdded}
                                    />
                                </div>
                            </div>
                        ),
                    }}
                    icons={ICONS_OVERRIDE_SECONDARY_COLOR}
                    localization={LOCALIZATION('pago', 'pagos')}
                    editable={{
                        // onRowAdd: (newData) => new Promise((resolve, reject) => {
                        //     handleOnRowAdd(newData, resolve, reject);
                        // }),
                        onRowUpdate: (newData, oldData) => new Promise((resolve, reject) => {
                            handleOnRowUpdate(newData, oldData, resolve, reject);
                        }),
                        onRowDelete: (oldData) => new Promise((resolve, reject) => {
                            handleOnRowDelete(oldData, resolve, reject);
                        }),
                    }}
                    actions={[
                        {
                            icon: 'printer',
                            tooltip: 'Imprimir Comprobante',
                            onClick: (event, rowData) => handleOpenPaymentPdf(event, rowData) // Default version
                        },
                        (rowData) => {
                            const concept = rowData.concept ? rowData.concept.toUpperCase() : '';
                            // Check if invoice is needed for the student AND if the concept matches
                            if (props.student?.invoiceNeeded && (concept.includes('INSCRIPCION') || concept.includes('REINSCRIPCION') || concept.includes('COLEGIATURA'))) {
                                return {
                                    icon: 'receipt', // Using 'receipt' icon for invoice
                                    tooltip: 'Generar Factura', // Updated tooltip
                                    onClick: (event, rowData) => handleGenerateInvoice(rowData) // Call new function
                                };
                            }
                            return null; // Don't show the action if conditions are not met
                        }
                    ]}


                />
                <ModalNewPayment
                    student={props.student}
                    grades={props.grades}
                    setIsLoading={setIsLoading}
                    onPaymentAdded={onPaymentAdded}
                />
                <ModalConfirmDeletePayment
                    open={deleteConfirmation.open}
                    setOpen={(open) => {
                        setDeleteConfirmation({ ...deleteConfirmation, open });
                    }}
                    onConfirm={confirmDeletePayment}
                    paymentData={deleteConfirmation.paymentData}
                />
            </div>
        );
    }

    return (
        <div className="Professors">
            <CustomizedSnackbars status={alertStatus} alertCallback={handleChangeOnAlert}/>
            {renderStudentPayments()}
        </div>
    );
};